import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  moduleReportShellQuery,
  moduleReportSummaryQuery,
  type ModuleReportSummary,
  type ReportModule,
} from '../../api/settingsModuleReports'
import { AnimatedNumber } from '../../alarms/AlarmDetails'
import { DatePicker } from '../../ui/DatePicker'
import { Forbidden } from '../../ui/Forbidden'
import { Spinner } from '../../ui/Spinner'
import { LoadFailed } from '../../ui/LoadFailed'
import { CompanyReportsFrame } from './CompanyReportsPage'
import { centered, esc, ReportTable, type ReportColumn } from './ReportTable'

/**
 * Settings -> Reports, filele pe module: `settings/company_reports/<modul>_report`.
 *
 * MACHETELE VECHI, element cu element:
 *  - `views/version-3/settings/company_reports/<modul>/index.php`: titlul
 *    (`<h3><strong>Modul</strong> report</h3>`), casuta de perioada
 *    (`.reportrange`, un daterangepicker cu sase scurtaturi), apoi sumarul si
 *    cate un `panel panel-<modul>` pentru fiecare tabel;
 *  - `.../<modul>/summary.php`: cardurile `dashboard-stat` cu cifra animata
 *    (`animateNumber`, aici `AnimatedNumber`) si legatura "View more";
 *  - `public/js/settings/company_reports.js`: coloanele fiecarui tabel
 *    (`<Modul>...Model`) si `initReportsTable`, cu markupul din `ReportTable`.
 *
 * Toate cinci filele sunt ACEEASI pagina cu alt tabel de configurare
 * (`MODULES` mai jos): ce se schimba de la una la alta e titlul, cardurile si
 * coloanele - nu purtarea.
 *
 * "VIEW MORE". Cardurile vechi trimiteau la ecranele modulului
 * (`inventoree/live_devices`, `printeeng/all_devices`...). Legatura se scrie
 * numai cand ecranul ala EXISTA in v4; altfel ar fi o legatura moarta spre un
 * portal care nu mai e pe server, si legaturile moarte se scot, nu se
 * reconstruiesc (`rawLinks.test.ts`).
 *
 * PERIOADA. `daterangepicker` nu e in v4; casuta `.reportrange` pastreaza
 * locul, culorile si textul ("September 1, 2026 - September 11, 2026"), iar
 * la apasare deschide cele sase scurtaturi ale machetei si doua calendare.
 * Pagina porneste pe ziua de azi, ca macheta (`date('Y-m-d')`).
 */

type Card = {
  key: string
  label: string
  icon: string
  /** Culoarea `dashboard-stat` din summary.php (`brown`, `yellow-mint`...). */
  color: string
  /** `col-md-4`, `col-md-3`, `col-md-2` - cate carduri incap pe rand. */
  width: string
  /** Cifra urca de la 0 (`data-number`); textele (ore, sume cu moneda) nu. */
  animated: boolean
  /** Ecranul v4 din "View more", fara `/app`; gol = fara legatura. */
  more: string
  /** Cum se scrie valoarea, cand nu e un numar gol. */
  text?(counters: ModuleReportSummary['counters']): string
}

type Panel = {
  /** Cheia tabelului pe server (`ModuleReports::TABLES`). */
  table: string
  /** `id`-ul tabelului din macheta veche. */
  id: string
  heading: string
  columns: ReportColumn[]
  order: { column: number; dir: 'asc' | 'desc' }
  severity?: number
}

type ModuleConfig = {
  title: string
  cards: Card[]
  panels: Panel[]
}

const text = (key: string) => (row: Record<string, unknown>) => esc(row[key])

/**
 * `ProactiviteeOpenedAlarmsModel` / `ClosedAlarmsModel`, coloana Value:
 * `value_numeric + sign` cand alarma e dinamica, `-` altfel;
 * `temperatureConvert(x, true)` din general.js pentru `convert = TEMP`.
 */
function alarmValue(row: Record<string, unknown>): string {
  if (Number(row.dinamic) !== 1) return '-'
  if (String(row.convert) === 'TEMP') return esc(`${Math.round(Number(row.value_numeric))} °C`)
  return esc(`${String(row.value_numeric ?? '')}${String(row.sign ?? '')}`)
}

const alarmColumns = (closed: boolean): ReportColumn[] => [
  { title: 'Device name', html: text('device_name') },
  { title: 'Device owner', html: text('device_owner') },
  { title: 'Alert type', html: text('alert_type') },
  { title: 'Value', html: alarmValue },
  { title: 'Created', html: text('created') },
  ...(closed ? [{ title: 'Closed', html: text('resolved') }] : []),
]

const securiteeFileColumns: ReportColumn[] = [
  { title: 'Logged User', html: text('username') },
  { title: 'Device Name', html: text('device_name') },
  { title: 'File name', html: text('file_name') },
  { title: 'File destination', html: text('file_destination') },
  { title: 'File size', html: text('file_size') },
  { title: 'Destination source', html: text('file_source') },
  { title: 'Device Date', html: text('device_date') },
  { title: 'Date', html: text('server_date') },
]

/** `ProductiviteeModel`: o medie lipsa se scrie 0, ca in model. */
const orZero = (key: string) => (row: Record<string, unknown>) => (row[key] ? esc(row[key]) : '0')

const MODULES: Record<ReportModule, ModuleConfig> = {
  inventoree: {
    title: 'Inventoree',
    cards: [
      { key: 'new_devices', label: 'New Devices', icon: 'fa fa-eye', color: 'brown', width: 'col-md-4', animated: true, more: 'inventoree/live_devices' },
      { key: 'decommissioned_devices', label: 'Decommissioned Devices', icon: 'fa fa-cubes', color: 'brown', width: 'col-md-4', animated: true, more: 'inventoree/decommissioned' },
      { key: 'devices_changes', label: 'Devices Changes', icon: 'fas fa-exclamation-triangle', color: 'brown', width: 'col-md-4', animated: true, more: 'inventoree/live_devices' },
      { key: 'countEndpointPerformanceAlerts', label: 'Endpoint Performance Alerts', icon: 'fas fa-exclamation-triangle', color: 'brown', width: 'col-md-4', animated: true, more: 'inventoree/live_devices' },
    ],
    panels: [
      {
        table: 'new-devices',
        id: 'newDevices',
        heading: 'New Devices',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Device type', html: text('Type') },
          { title: 'Device name', html: text('Name') },
          { title: 'Device owner', html: text('DeviceOwner') },
          { title: 'Date', html: text('AddDate') },
        ],
      },
      {
        table: 'decommissioned-devices',
        id: 'decommissionedDevices',
        heading: 'Decommissioned Devices',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Device type', html: text('Type') },
          { title: 'Device name', html: text('Name') },
          { title: 'Device owner', html: text('DeviceOwner') },
          { title: 'Date', html: text('RemoveDate') },
        ],
      },
      {
        table: 'devices-changes',
        id: 'devicesChanges',
        heading: 'Devices Changes',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Device type', html: text('Type') },
          { title: 'Device name', html: text('Name') },
          { title: 'Device owner', html: text('DeviceOwner') },
          { title: 'Event', html: text('Event') },
          { title: 'Date', html: text('EventDate') },
        ],
      },
      {
        table: 'endpoint-performance-alerts',
        id: 'endpointPerformanceAlarms',
        heading: 'Endpoint Performance Alarms',
        order: { column: 3, dir: 'desc' },
        columns: [
          { title: 'Alarm type', html: text('type') },
          { title: 'Value', html: text('value') },
          { title: 'Device owner', html: text('DeviceOwner') },
          { title: 'Date', html: text('startDate') },
        ],
      },
    ],
  },
  printeeng: {
    title: 'Printeeng',
    cards: [
      { key: 'total_pages', label: 'Total Printed Pages', icon: 'fa fa-file', color: 'yellow-mint', width: 'col-md-3', animated: true, more: '' },
      {
        key: 'total_cost',
        label: 'Total Cost',
        icon: 'fas fa-money-bill-alt',
        color: 'yellow-mint',
        width: 'col-md-3',
        animated: false,
        more: '',
        /* `total_cost . ' ' . currency`, ca in summary.php. */
        text: (c) => `${String(c.total_cost ?? 0)} ${String(c.currency ?? '')}`,
      },
      { key: 'active_printers', label: 'Active Printers', icon: 'fa fa-print', color: 'yellow-mint', width: 'col-md-3', animated: false, more: '' },
      { key: 'users', label: 'Users Who Printed Files', icon: 'fa fa-user', color: 'yellow-mint', width: 'col-md-3', animated: true, more: '' },
    ],
    panels: [
      {
        table: 'costs',
        id: 'costs',
        heading: 'Printers',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Printer Name', html: text('printer_name') },
          { title: 'Toner Type', html: (row) => centered(TONER_TYPES[String(row.toner_type)] ?? 'Original') },
          { title: 'Printed Pages', html: (row) => centered(row.printed_pages) },
          { title: 'Printing Cost', html: (row) => centered(`${String(row.cost ?? '')} ${String(row.currency ?? '')}`) },
          {
            title: 'Cost based on',
            html: (row) =>
              Number(row.pricing_type) === 2
                ? centered(`Toner price/estimated no. of pages to print (${String(row.toner_price)} ${String(row.currency ?? '')}/${String(row.toner_pages)} pages)`)
                : centered(`Cost/page (${String(row.price_per_page)} ${String(row.currency ?? '')}/page)`),
          },
        ],
      },
      {
        table: 'total-files',
        id: 'totalFiles',
        heading: 'Total Files',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Logged User', html: text('logged_user') },
          { title: 'Device Name', html: text('device_name') },
          { title: 'Pages', html: (row) => centered(row.pages) },
          { title: 'Cost', html: (row) => centered(row.cost) },
          { title: 'Printing Type', html: text('printing_type') },
          { title: 'File Name', html: text('file_name') },
          { title: 'Date', html: (row) => centered(row.date) },
        ],
      },
      {
        table: 'users',
        id: 'usersFiles',
        heading: 'Users Who Printed Files',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Logged User', html: text('logged_user') },
          { title: 'Printed Pages', html: (row) => centered(row.printed_pages) },
          { title: 'Cost', html: (row) => centered(row.cost) },
        ],
      },
    ],
  },
  securitee: {
    title: 'Securitee',
    cards: [
      { key: 'total_files', label: 'Total Files', icon: 'fa fa-folder', color: 'blue-madison', width: 'col-md-4', animated: true, more: 'securitee/all_devices' },
      { key: 'documents', label: 'Documents', icon: 'fa fa-file', color: 'blue-madison', width: 'col-md-4', animated: true, more: 'securitee/all_devices' },
      { key: 'users', label: 'Users who transferred files', icon: 'fa fa-user', color: 'blue-madison', width: 'col-md-4', animated: true, more: 'securitee/all_devices' },
    ],
    panels: [
      { table: 'total-files', id: 'totalFiles', heading: 'Total Files', order: { column: 0, dir: 'desc' }, columns: securiteeFileColumns },
      { table: 'documents', id: 'documents', heading: 'Documents', order: { column: 0, dir: 'desc' }, columns: securiteeFileColumns },
      {
        table: 'users',
        id: 'usersFiles',
        heading: 'Users who transferred files',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'User name', html: text('username') },
          { title: 'Total files', html: orZero('files') },
          { title: 'Total size', html: orZero('files_size') },
          { title: 'Documents', html: orZero('documents') },
          { title: 'Documents size', html: orZero('documents_size') },
        ],
      },
    ],
  },
  productivitee: {
    title: 'Productivitee',
    cards: [
      /* Mediile vin in cuvinte (`nice_time`) - nu urca de la 0. */
      { key: 'productive_time', label: 'Avg. Productive Time', icon: 'fas fa-chart-line', color: 'yellow', width: 'col-md-3', animated: false, more: 'productivitee/device_productivity' },
      { key: 'active_time', label: 'Avg. Active Time', icon: 'fa fa-hourglass-half', color: 'yellow', width: 'col-md-3', animated: false, more: 'productivitee/device_productivity' },
      { key: 'working_time', label: 'Avg. Working Time', icon: 'far fa-clock', color: 'yellow', width: 'col-md-3', animated: false, more: 'productivitee/device_productivity' },
      { key: 'users', label: 'Present Users', icon: 'fa fa-user', color: 'yellow', width: 'col-md-3', animated: false, more: 'productivitee/device_productivity' },
    ],
    panels: [
      {
        table: 'devices',
        id: 'productiviteeReport',
        heading: 'Devices Productivitee',
        order: { column: 0, dir: 'desc' },
        columns: [
          { title: 'Device name', html: text('device_name') },
          { title: 'Logged user', html: text('logged_user') },
          { title: 'Avg. Productive Time', html: orZero('productive_time') },
          { title: 'Avg. Active Time', html: orZero('active_time') },
          { title: 'Avg. Working Time', html: orZero('working_time') },
          { title: 'Avg. Starting Time', html: text('starting_time') },
          { title: 'Avg. Ending Time', html: text('ending_time') },
        ],
      },
      {
        table: 'users',
        id: 'productiviteeUsersReport',
        heading: 'Users Productivitee',
        /* Singurul tabel al celor cinci file care pornea CRESCATOR:
           `productivitee/index.php` il deschide cu `[[0, 'asc']]`. */
        order: { column: 0, dir: 'asc' },
        columns: [
          { title: 'Logged user', html: text('logged_user') },
          { title: 'Avg. Active Time', html: orZero('active_time') },
          { title: 'Avg. Working Time', html: orZero('working_time') },
          { title: 'Avg. Starting Time', html: text('starting_time') },
          { title: 'Avg. Ending Time', html: text('ending_time') },
        ],
      },
    ],
  },
  proactivitee: {
    title: 'Proactivitee',
    cards: [
      { key: 'criticalAlarms', label: 'Opened Critical Alarms', icon: 'fas fa-exclamation-circle', color: 'red-intense', width: 'col-md-2', animated: true, more: 'proactivitee/alarms' },
      { key: 'majorAlarms', label: 'Opened Major Alarms', icon: 'fas fa-exclamation-triangle', color: 'yellow', width: 'col-md-2', animated: true, more: 'proactivitee/alarms' },
      { key: 'minorAlarms', label: 'Opened Minor Alarms', icon: 'fa fa-bell', color: 'green-haze', width: 'col-md-2', animated: true, more: 'proactivitee/alarms' },
      { key: 'infoAlarms', label: 'Opened Info Alarms', icon: 'fas fa-info-circle', color: 'blue-madison', width: 'col-md-2', animated: true, more: 'proactivitee/alarms' },
      { key: 'closedAlarmsByUser', label: 'Closed by User', icon: 'fa fa-user', color: 'red-intense', width: 'col-md-2', animated: true, more: 'proactivitee/alarms' },
      { key: 'autoClosedAlarms', label: 'Auto Closed', icon: 'fa fa-bell', color: 'red-intense', width: 'col-md-2', animated: true, more: 'proactivitee/alarms' },
    ],
    panels: [
      /* `getOpenedAlarmsData/{severity}`: acelasi tabel, de patru ori. */
      { table: 'opened-alarms', id: 'criticalAlarms', heading: 'Opened Critical Alarms', order: { column: 0, dir: 'desc' }, columns: alarmColumns(false), severity: 3 },
      { table: 'opened-alarms', id: 'majorAlarms', heading: 'Opened Major Alarms', order: { column: 0, dir: 'desc' }, columns: alarmColumns(false), severity: 2 },
      { table: 'opened-alarms', id: 'minorAlarms', heading: 'Opened Minor Alarms', order: { column: 0, dir: 'desc' }, columns: alarmColumns(false), severity: 1 },
      { table: 'opened-alarms', id: 'infoAlarms', heading: 'Opened Info Alarms', order: { column: 0, dir: 'desc' }, columns: alarmColumns(false), severity: 0 },
      { table: 'closed-alarms', id: 'closedAlarms', heading: 'Resolved Alarms', order: { column: 0, dir: 'desc' }, columns: alarmColumns(true) },
    ],
  },
}

/** `PrinteengCostsModel`: felul tonerului, pe cod; orice altceva e "Original". */
const TONER_TYPES: Record<string, string> = { '1': 'Original', '2': 'Recharged', '3': 'Compatible' }

/* Cele sase scurtaturi ale daterangepicker-ului din macheta, cu aceleasi zile. */

function isoDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function shiftDays(days: number, now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days)
  return isoDay(d)
}

function monthEdge(monthsBack: number, edge: 'start' | 'end', now = new Date()): string {
  const d = edge === 'start'
    ? new Date(now.getFullYear(), now.getMonth() - monthsBack, 1)
    : new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 0)
  return isoDay(d)
}

export function reportRanges(now = new Date()): { label: string; from: string; to: string }[] {
  return [
    { label: 'Today', from: shiftDays(0, now), to: shiftDays(0, now) },
    { label: 'Yesterday', from: shiftDays(-1, now), to: shiftDays(-1, now) },
    { label: 'Last 7 Days', from: shiftDays(-6, now), to: shiftDays(0, now) },
    { label: 'Last 30 Days', from: shiftDays(-29, now), to: shiftDays(0, now) },
    { label: 'This Month', from: monthEdge(0, 'start', now), to: monthEdge(0, 'end', now) },
    { label: 'Last Month', from: monthEdge(1, 'start', now), to: monthEdge(1, 'end', now) },
  ]
}

/** `moment(d).format('MMMM D, YYYY')`, cum scria macheta in `.reportrange span`. */
export function longDay(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m) return day
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

export function ReportRange({ from, to, onChange }: { from: string; to: string; onChange(from: string, to: string): void }) {
  const [open, setOpen] = useState(false)
  const ranges = useMemo(() => reportRanges(), [])
  const label = from !== '' && to !== '' ? `${longDay(from)} - ${longDay(to)}` : 'Select your date range'

  return (
    <div style={{ position: 'relative' }}>
      <div
        className="reportrange"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        style={{ cursor: 'pointer', padding: 8, color: '#FFF', background: '#616F7B' }}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
      >
        <i className="fas fa-calendar" aria-hidden="true"></i>&nbsp;
        <span style={{ textTransform: 'uppercase' }}>{label}</span> <b className="caret"></b>
        <input type="hidden" name="filterFrom" id="filterFrom" value={from} readOnly />
        <input type="hidden" name="filterTo" id="filterTo" value={to} readOnly />
      </div>
      {open ? (
        <div
          className="daterangepicker dropdown-menu opensleft show-calendar"
          style={{ display: 'block', position: 'absolute', right: 0, top: '100%', zIndex: 1000, padding: 10, minWidth: 320 }}
        >
          <div className="ranges">
            <ul className="list-unstyled">
              {ranges.map((r) => (
                <li
                  key={r.label}
                  className={r.from === from && r.to === to ? 'active' : ''}
                  style={{ cursor: 'pointer', padding: '3px 8px' }}
                  onClick={() => {
                    onChange(r.from, r.to)
                    setOpen(false)
                  }}
                >
                  {r.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="form-inline" style={{ marginTop: 8 }}>
            <div className="form-group" style={{ marginRight: 8 }}>
              <label htmlFor="reportFrom">From&nbsp;</label>
              <DatePicker id="reportFrom" name="reportFrom" value={from} onChange={(next) => onChange(next, to)} typed />
            </div>
            <div className="form-group">
              <label htmlFor="reportTo">To&nbsp;</label>
              <DatePicker id="reportTo" name="reportTo" value={to} onChange={(next) => onChange(from, next)} typed />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function SummaryCards({ module, cards, from, to }: { module: ReportModule; cards: Card[]; from: string; to: string }) {
  const summary = useQuery(moduleReportSummaryQuery(module, from, to))

  if (summary.error instanceof ApiError && summary.error.status === 403) return <Forbidden />
  if (summary.error) return <LoadFailed what="The summary" error={summary.error} onRetry={() => void summary.refetch()} />
  if (!summary.data) return <Spinner />

  const counters = summary.data.counters

  return (
    <div className="row">
      {cards.map((card) => (
        <div className={card.width} key={card.key}>
          <div className={`dashboard-stat ${card.color}`}>
            <div className="visual">
              <i className={card.icon} aria-hidden="true"></i>
            </div>
            <div className="details">
              {card.animated ? (
                <AnimatedNumber value={Number(counters[card.key] ?? 0)} />
              ) : (
                <div className="number">{card.text ? card.text(counters) : String(counters[card.key] ?? 0)}</div>
              )}
              <div className="desc">{card.label}</div>
            </div>
            {card.more !== '' ? (
              <a className="more" href={'/app/' + card.more}>
                View more <i className="fa fa-arrow-circle-right" aria-hidden="true"></i>
              </a>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}

export function ModuleReportPage({ module }: { module: ReportModule }) {
  const config = MODULES[module]
  const shell = useQuery(moduleReportShellQuery(module))
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null)

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />

  /* Ziua de azi vine de la server, ca in macheta (`date('Y-m-d')`). */
  const from = period?.from ?? shell.data?.today ?? ''
  const to = period?.to ?? shell.data?.today ?? ''

  let body: ReactNode
  if (shell.error) {
    body = <LoadFailed what="This report" error={shell.error} onRetry={() => void shell.refetch()} />
  } else if (!shell.data) {
    body = <Spinner />
  } else if (!shell.data.allowed) {
    body = (
      <div className="alert alert-danger">
        <i className="fa fa-ban" aria-hidden="true"></i> Access denied.
      </div>
    )
  } else {
    body = (
      <div className="row">
        <div className={`${module}-summary col-md-12`}>
          <SummaryCards module={module} cards={config.cards} from={from} to={to} />
        </div>
        {config.panels.map((panel) => (
          <div className="col-md-12" key={panel.id}>
            <div className={`panel panel-${module}`}>
              <div className="panel-heading">{panel.heading}</div>
              <div className="panel-body" style={{ padding: 5 }}>
                <ReportTable
                  module={module}
                  table={panel.table}
                  id={panel.id}
                  columns={panel.columns}
                  order={panel.order}
                  from={from}
                  to={to}
                  severity={panel.severity}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <CompanyReportsFrame current={module}>
      <div id="content">
        <div className="row" style={{ marginBottom: 20 }}>
          <div className="col-md-9">
            <h3>
              <strong>{config.title}</strong> report
            </h3>
          </div>
          <div className="col-md-3">
            <ReportRange from={from} to={to} onChange={(f, t) => setPeriod({ from: f, to: t })} />
          </div>
        </div>
        {body}
      </div>
    </CompanyReportsFrame>
  )
}

export function InventoreeReportPage() {
  return <ModuleReportPage module="inventoree" />
}

export function PrinteengReportPage() {
  return <ModuleReportPage module="printeeng" />
}

export function SecuriteeReportPage() {
  return <ModuleReportPage module="securitee" />
}

export function ProductiviteeReportPage() {
  return <ModuleReportPage module="productivitee" />
}

export function ProactiviteeReportPage() {
  return <ModuleReportPage module="proactivitee" />
}
